import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import EventEHRLazyDropdown from "../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import ClearFiltersButton from "../components/ClearFiltersButton";
import DownloadCsvButton from "../components/DownloadCsvButton";
import { searchIcon } from "../../../assets";
import ReportsService from "../../../services/ReportsService";
import FacilityManagementService from "../../../services/FacilityManagementService";
import { componentKey } from "../ReportsSlice";
import { getProviderPatientLog } from "../ReportsSaga";
import {
  CSV_HEADERS_PROVIDER_PATIENT_LOG,
  ROWS_PER_PAGE_OPTIONS,
  formatEncounterDate,
  formatVisitType,
  toApiStartDate,
  toApiEndDate,
} from "../ReportConstants";

type ProviderPatientLogTabProps = { facilityId: string };

type ProviderOption = { label: string; value: string };

// One scroll page of the provider picker. Small on purpose: it has to overflow
// the shared dropdown's 200px listbox, which is what makes the scroll-to-bottom
// handler fire at all. Matches UnitNumberSelect's PAGE_LIMIT.
const PROVIDER_PAGE_SIZE = 25;

const mapProviderOptions = (
  users: Array<Record<string, any>>,
): ProviderOption[] =>
  users
    .map((user) => {
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      return {
        label: user?.facility?.name
          ? `${name} (${user?.facility?.name})`
          : name,
        value: user?.id,
      };
    })
    .filter((option) => option.value && option.label);

// A supervised encounter: one person wrote the note, another signed it off.
// Compares ids when the API sends them and falls back to names when it does
// not, so the "Documented by" line never silently disappears on a response
// that carries only the display names.
const isCoSigned = (row: Record<string, any>) => {
  if (!row?.documentedByName || !row?.signedByName) return false;
  if (row?.documentedById && row?.signedById) {
    return row.documentedById !== row.signedById;
  }
  return row.documentedByName !== row.signedByName;
};

/**
 * Provider Patient Log.
 * One row per encounter: the patients a provider saw, with the date, facility,
 * chief complaint and ICD-10/DSM diagnosis — the encounter log supervised
 * trainees (military residents, university students/residents) need for their
 * programme requirements.
 *
 * A provider matches when they signed OR documented the note, because a
 * resident typically writes the note their attending signs off.
 */
const ProviderPatientLogTab = ({ facilityId }: ProviderPatientLogTabProps) => {
  const dispatch = useDispatch();
  const { providerPatientLog } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>,
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderOption | null>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Provider picker paging state lives in refs, not state, on purpose — see the
  // note on handleProviderScrollEnd.
  const providerPageRef = useRef(1); // last page successfully loaded
  const providerTotalPagesRef = useRef(1); // from the last response
  const providerSearchRef = useRef(""); // committed (debounced) `name` term
  const providerLoadingRef = useRef(false); // in-flight guard for scroll paging
  const providerRequestIdRef = useRef(0); // discards superseded responses
  const providerSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const rows: Array<Record<string, any>> = providerPatientLog?.data || [];

  // The picker is filled from the shared user-list API (`GET user`), the same
  // one the other staff dropdowns use — the report endpoint returns rows only.
  //
  // Fetched WITHOUT a `type` filter on purpose: a note's author or signer is
  // not always registered as type "provider" (supervising staff and admins
  // document encounters too), and `type=provider` would leave those people out
  // of the picker entirely — silently hiding every encounter they ran.
  //
  // And WITHOUT a `status` filter. This report is run AFTER an event, once
  // staff accounts have been deactivated — but a disabled login does not erase
  // the encounters that person ran. Filtering to status "Active" left only the
  // admin accounts still in use, which made every departed provider's log
  // unreachable from the picker.
  //
  // Sent with `includeAdminUsers=true` so the list also carries admin_users
  // records, at the client's request: they want every account in the system
  // offered here. Note that a person who exists ONLY in admin_users can never
  // appear on a visit note (the visit_note foreign keys point at the user
  // table), so selecting one always returns an empty log — the API handles it
  // safely, it just has no encounters to show.
  //
  // Also NOT scoped by the screen's Facility selector. That selector narrows
  // rows by `seenFacility` — where the encounter happened — while a user's
  // `facilityId` is their home facility. A provider based at one facility can
  // well have seen patients at another, so filtering the picker by facility
  // would hide people who do have encounters in the selected scope.

  // Loaded one page at a time: the picker starts on page 1 and appends the next
  // page each time the listbox is scrolled to the bottom, and typing runs a
  // debounced server-side `name` search so a provider who has not been paged in
  // yet is still findable. Rows arrive in the API's own order — the list is not
  // re-sorted client-side, because sorting an accumulating buffer re-inserts
  // newly appended rows above the viewport and yanks the scroll position.
  const loadProviders = async (
    page: number,
    search: string,
    reset: boolean,
  ) => {
    const requestId = ++providerRequestIdRef.current;
    // Set synchronously, before the first await, so a duplicate scroll event in
    // the same tick cannot start a second request for the same page.
    providerLoadingRef.current = true;
    providerSearchRef.current = search;
    try {
      const response = await FacilityManagementService.getFacilityUserList({
        pageNumber: page,
        limit: PROVIDER_PAGE_SIZE,
        includeAdminUsers: true,
        // getFacilityUserList interpolates values straight into the query
        // string without encoding them, so a name carrying `&`, `#` or `+` has
        // to arrive already encoded.
        ...(search ? { name: encodeURIComponent(search) } : {}),
      });
      // A newer search or reset has been issued — this response is stale.
      if (requestId !== providerRequestIdRef.current) return;
      const options = mapProviderOptions(response?.data?.users ?? []);
      providerTotalPagesRef.current = response?.data?.totalPages ?? 1;
      // Advanced only on success, so a failed page is retried on the next
      // scroll rather than silently skipped.
      providerPageRef.current = page;
      setProviderOptions((prev) =>
        reset
          ? options
          : prev.concat(
              options.filter((o) => !prev.some((p) => p.value === o.value)),
            ),
      );
    } catch {
      if (requestId === providerRequestIdRef.current && reset) {
        providerPageRef.current = 1;
        providerTotalPagesRef.current = 1;
        setProviderOptions([]);
      }
    } finally {
      if (requestId === providerRequestIdRef.current) {
        providerLoadingRef.current = false;
      }
    }
  };

  // The listbox was scrolled to the bottom. The updater EventEHRLazyDropdown
  // passes is ignored on purpose: it attaches a fresh scroll listener on every
  // open without removing the previous one, so this can fire twice for a single
  // scroll — and a `(count) => count + 1` updater would then jump 1 -> 3 and
  // skip a page entirely. Deriving the next page from a ref keeps it idempotent.
  const handleProviderScrollEnd = () => {
    if (providerLoadingRef.current) return;
    if (providerPageRef.current >= providerTotalPagesRef.current) return;
    loadProviders(
      providerPageRef.current + 1,
      providerSearchRef.current,
      false,
    );
  };

  const handleProviderSearch = (value: string) => {
    if (providerSearchTimeoutRef.current) {
      clearTimeout(providerSearchTimeoutRef.current);
    }
    const term = (value || "").trim();
    providerSearchTimeoutRef.current = setTimeout(() => {
      if (term === providerSearchRef.current) return;
      loadProviders(1, term, true);
    }, 500);
  };

  // Popup closed, input emptied or blurred — back to the unfiltered first page.
  const resetProviderList = () => {
    if (providerSearchTimeoutRef.current) {
      clearTimeout(providerSearchTimeoutRef.current);
    }
    // This fires on every close and every blur, so skip the request when the
    // list is already unfiltered and unpaged.
    if (
      !providerSearchRef.current &&
      providerPageRef.current === 1 &&
      providerOptions.length
    ) {
      return;
    }
    loadProviders(1, "", true);
  };

  useEffect(() => {
    loadProviders(1, "", true);
  }, []);

  // Keep the selection in the list. After a search replaces the options the
  // selected provider would otherwise vanish, and MUI's default
  // isOptionEqualToValue is reference equality — so the *same object* has to be
  // the one present, or the listbox resets its scroll to the top on append.
  const providerDropdownOptions = useMemo(
    () =>
      selectedProvider
        ? [
            selectedProvider,
            ...providerOptions.filter(
              (o) => o.value !== selectedProvider.value,
            ),
          ]
        : providerOptions,
    [providerOptions, selectedProvider],
  );

  const queryParams = {
    providerId: selectedProvider?.value || undefined,
    // The shared facility selector scopes the log through `seenFacility` —
    // the endpoint ignores `facilityId` by design.
    seenFacility: facilityId || undefined,
    searchValue: searchedValue?.trim() || undefined,
    startDate: toApiStartDate(startDate),
    endDate: toApiEndDate(endDate),
  };

  useEffect(() => {
    dispatch(
      getProviderPatientLog({
        pageNumber,
        limit: rowPerPage,
        ...queryParams,
      }),
    );
  }, [
    facilityId,
    pageNumber,
    rowPerPage,
    searchedValue,
    selectedProvider,
    startDate,
    endDate,
  ]);

  // Drop pending debounces if the tab is unmounted mid-typing (switching
  // report tabs unmounts this component).
  useEffect(
    () => () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (providerSearchTimeoutRef.current) {
        clearTimeout(providerSearchTimeoutRef.current);
      }
    },
    [],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(
    searchedValue || selectedProvider || startDate || endDate,
  );

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setSelectedProvider(null);
    // Drop any search results the picker is still showing, so reopening it
    // starts from the unfiltered first page.
    resetProviderList();
    setStartDate(null);
    setEndDate(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "providerName",
      label: <div>Provider</div>,
      renderLogic: (row: Record<string, any>) => (
        <div>
          <p className="text-neutral-90 font-weight-500">
            {row?.providerName || "-"}
          </p>
          {/* Surfaced only when the encounter was supervised — the resident
              wrote it and someone else signed it off. */}
          {isCoSigned(row) && (
            <p className="text-neutral-70 text-xs">
              Documented by {row?.documentedByName}
            </p>
          )}
        </div>
      ),
    },
    {
      field: "patientName",
      label: <div>Patient</div>,
      renderLogic: (row: Record<string, any>) => (
        <div>
          <p className="text-neutral-90 font-weight-500">
            {row?.patientName || "-"}
          </p>
          {row?.bsaId && (
            <p className="text-neutral-70 text-xs">BSA ID: {row?.bsaId}</p>
          )}
        </div>
      ),
    },
    {
      field: "encounterDate",
      label: <div>Date of Encounter</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatEncounterDate(row?.encounterDate)}
        </p>
      ),
    },
    {
      field: "facilityName",
      label: <div>Facility/Location</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.facilityName || "Unassigned"}
        </p>
      ),
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: Record<string, any>) => (
        <Tooltip
          arrow
          placement="bottom-start"
          title={row?.chiefComplaint || ""}
        >
          <p className="text-neutral-90 font-normal max-w-[220px] truncate">
            {row?.chiefComplaint || "-"}
          </p>
        </Tooltip>
      ),
    },
    {
      field: "diagnosisText",
      label: <div>ICD-10 / DSM Diagnosis</div>,
      renderLogic: (row: Record<string, any>) => (
        <Tooltip
          arrow
          placement="bottom-start"
          title={row?.diagnosisText || ""}
        >
          <p className="text-neutral-90 font-normal max-w-[280px] truncate">
            {row?.diagnosisText || "-"}
          </p>
        </Tooltip>
      ),
    },
    {
      field: "isSigned",
      label: <div>Note</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.isSigned ? "Signed" : "Unsigned"}
        </p>
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getProviderPatientLog({
      limit: "All",
      ...queryParams,
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      providerName: row?.providerName || "",
      patientName: row?.patientName || "",
      bsaId: row?.bsaId || "",
      encounterDate: formatEncounterDate(row?.encounterDate),
      facilityName: row?.facilityName || "Unassigned",
      chiefComplaint: row?.chiefComplaint || "",
      diagnosisText: row?.diagnosisText || "",
      noteStatus: row?.isSigned ? "Signed" : "Unsigned",
      documentedByName: row?.documentedByName || "",
      signedByName: row?.signedByName || "",
      // Same friendly labels the other report exports use — the raw API values
      // ("WALK_In_Visit", "EMS_Visit", "Scene") are not fit for a filed log.
      visitType: row?.visitType ? formatVisitType(row?.visitType) : "",
    }));
  };

  // Provider label without the "(Facility)" suffix — used for the export
  // filename and the empty-state message.
  const selectedProviderName = selectedProvider?.label
    ?.replace(/\s*\(.*\)$/, "")
    .trim();

  // Name the export after the provider so a programme can file the downloaded
  // log without renaming it.
  const csvFilename = [
    "provider-patient-log",
    selectedProviderName
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      // A name ending in punctuation ("John Doe Jr.") would otherwise leave a
      // trailing dash right before the extension.
      .replace(/^-+|-+$/g, ""),
  ]
    .filter(Boolean)
    .join("-")
    .concat(".csv");

  // Spell out which filters produced an empty result. The provider picker is
  // deliberately not facility-scoped (see the loader above), so "no rows" after
  // picking a provider is most often a scope/date mismatch rather than a
  // provider with no encounters at all.
  const noDataFoundMessage = selectedProviderName
    ? `No encounters found for ${selectedProviderName}${
        facilityId ? " at the selected facility" : ""
      }${startDate || endDate ? " in this date range" : ""}`
    : "No encounters found";

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search Patient / Complaint / Diagnosis"
          fullWidth={"280px"}
          startIcon={
            <img
              alt="search-icon"
              src={searchIcon}
              width={"18px"}
              height={"18px"}
            />
          }
        />
        <div style={{ minWidth: "250px" }}>
          {/* EventEHRLazyDropdown finds its listbox by the hardcoded id
              "#lazyDropdownSearch-listbox", so only one may be mounted per
              screen — the report tabs render one at a time.

              `dropdownIcon` is load-bearing: its false branch sets
              endAdornment to null, which would strip both the arrow and the
              clear X. `filterOptions` passthrough disables MUI's client-side
              filter, which would otherwise reduce the reopened list to the one
              row matching the selected provider's full label. `dropdownSearch`
              is a literal "" so the component's clear branch always nulls the
              selection; the live term lives in providerSearchRef. */}
          <EventEHRLazyDropdown
            label="Provider"
            placeholder="All Providers"
            noOptionText="No providers found"
            dropdownIcon
            clearSearch
            filterOptions={(options: any[]) => options}
            dropdownOptions={providerDropdownOptions}
            dropdownSearch=""
            selectedValue={selectedProvider}
            setDropdownSearch={handleProviderSearch}
            setPageCount={handleProviderScrollEnd}
            setResetFlag={(isReset: boolean) => {
              if (isReset) resetProviderList();
            }}
            setSelectedValue={(option: any) => {
              setPageNumber(1);
              setSelectedProvider(option ?? null);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setStartDate(value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setEndDate(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_PROVIDER_PATIENT_LOG}
          filename={csvFilename}
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage={noDataFoundMessage}
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: providerPatientLog?.totalPages,
          totalCount: providerPatientLog?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default ProviderPatientLogTab;
